import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TableComponent } from '../../../../shared/component/table/table.component';
import { AgGridTableService } from '../../../../shared/services/ag-grid.service';
import { ReportPaginationService, ReportPayload } from '../../Reports/report-pagination.service';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-evc-charging-history',
  standalone: true,
  imports: [CommonModule, TableComponent],
  providers: [AgGridTableService, ReportPaginationService],
  template: `
    <div class="evc-section">
      <div class="evc-sec-head">
        <div class="evc-sec-title">
          <span class="evc-sec-accent"></span>
          <div>
            <span class="evc-sec-name">EV Charging History&nbsp;</span>
            <span class="evc-sec-sub">Past charging sessions, durations & energy delivered</span>
          </div>
        </div>
        <div class="view-toggle">
          <button class="vt-btn" [class.vt-active]="viewMode === 'grid'" (click)="setView('grid')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> Grid
          </button>
          <button class="vt-btn" [class.vt-active]="viewMode === 'chart'" (click)="setView('chart')">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> Chart
          </button>
        </div>
      </div>
      <div class="evc-content-area">
        <div class="evc-grid-wrap" [class.view-hidden]="viewMode !== 'grid'">
           <app-table 
             [pageNation]="false" 
             [DateFilterShow]="true" 
             [paginationPageSizeSelector]="[10, 20, 50]" 
             [maxRowCount]="totalCount" 
             (onPageChange)="handlePageChange($event)" 
             [ExportFileName]="exportFileName"
             (filtersApplied)="onFiltersApplied($event)">
           </app-table>
        </div>
        <div class="evc-chart-wrap" [class.view-hidden]="viewMode !== 'chart'">
          <canvas #chartCanvas class="evc-canvas"></canvas>
        </div>
      </div>
    </div>
  `,
  styleUrl: '../ev-charger.component.scss'
})
export class EvcChargingHistoryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() locationId: string = '';
  @Input() siteId: string = '';
  @Input() sensorId: string = '';
  @Input() evName?: string;

  @ViewChild('chartCanvas', { static: false }) chartCanvasRef?: ElementRef<HTMLCanvasElement>;

  viewMode: 'grid' | 'chart' = 'grid';
  chartInstance?: Chart;
  private rowData: any[] = [];
  fromDate: string = '';
  toDate: string = '';
  totalCount: number = 0;

  get exportFileName(): string {
    const namePart = this.evName ? ` ${this.evName}` : '';
    return `EV Charging History${namePart} ${new Date().toISOString().slice(0, 10)}`;
  }

  private destroy$ = new Subject<void>();
  private isDestroyed = false;
  private renderTimer: any = null;

  constructor(
    private tableService: AgGridTableService,
    public reportPaginationService: ReportPaginationService,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    if (!this.locationId) {
      this.locationId = localStorage.getItem('login_id') || localStorage.getItem('location_id') || '9440991006';
    }
    if (!this.siteId) {
      this.siteId = sessionStorage.getItem('consumerSiteId') || localStorage.getItem('selected_site_id') || localStorage.getItem('site_idLogin') || localStorage.getItem('site_id') || '59.64062087';
    }

    const dTo = new Date();
    const dFrom = new Date();
    dFrom.setDate(dTo.getDate() - 1);
    this.fromDate = dFrom.toISOString().split('T')[0];
    this.toDate = dTo.toISOString().split('T')[0];

    this.reportPaginationService.totalCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.totalCount = count;
      });

    this.tableService.tableData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (this.isDestroyed) return;
        this.rowData = (state?.rowData || []).filter(r => !r?.['isDummy'] && !r?.['_isTotal']);
        if (this.viewMode === 'chart' && this.rowData.length > 0) {
          if (this.renderTimer) clearTimeout(this.renderTimer);
          this.renderTimer = setTimeout(() => this.renderChart(), 80);
        }
      });

    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    const locChange = changes['locationId'];
    const siteChange = changes['siteId'];
    const sensorChange = changes['sensorId'];
    if ((locChange && !locChange.firstChange && locChange.currentValue) ||
      (siteChange && !siteChange.firstChange && siteChange.currentValue) ||
      (sensorChange && !sensorChange.firstChange && sensorChange.currentValue)) {
      this.loadData();
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    if (this.renderTimer) {
      clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }
  }

  setView(mode: 'grid' | 'chart') {
    this.viewMode = mode;
    if (mode === 'chart') {
      if (this.renderTimer) clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => this.renderChart(), 80);
    }
  }

  onFiltersApplied(event: { from_date: string; to_date: string; site_id?: string }) {
    if (event.from_date && event.to_date) {
      this.fromDate = event.from_date;
      this.toDate = event.to_date;
      if (event.site_id) {
        this.siteId = event.site_id;
      }
      this.loadData();
    }
  }

  handlePageChange(event: { page: number; pageSize: number }) {
    this.reportPaginationService.changePage(event, this.destroy$);
  }

  loadData() {
    const servicePayload: ReportPayload = {
      from_date: this.fromDate,
      to_date: this.toDate,
      site_ids: this.siteId,
      loc_id: this.locationId,
      sensor_id: this.sensorId,
      is_paginate: 'N',
    };

    this.reportPaginationService.initFilters(
      servicePayload,
      'EV_CHARGING_HISTORY',
      this.destroy$,
      '',
      'Reports',
      null,
      false
    );
  }

  private parseDurationMinutes(val: any, start?: string, stop?: string): number {
    if (typeof val === 'number') return val;
    if (typeof val === 'string' && val.trim()) {
      if (val.includes(':')) {
        const parts = val.trim().split(':').map(Number);
        if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
          return Math.round(parts[0] * 60 + parts[1] + parts[2] / 60);
        }
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          return Math.round(parts[0] * 60 + parts[1]);
        }
      }
      const num = parseFloat(val);
      if (!isNaN(num)) return num;
    }
    if (start && stop) {
      const d1 = new Date(start.replace(/-/g, '/')).getTime();
      const d2 = new Date(stop.replace(/-/g, '/')).getTime();
      if (!isNaN(d1) && !isNaN(d2) && d2 > d1) {
        return Math.round((d2 - d1) / 60000);
      }
    }
    return 0;
  }

  private formatSessionLabel(startTime: string, index: number): string {
    if (!startTime) return `Session #${index + 1}`;
    try {
      const parts = startTime.trim().split(' ');
      if (parts.length === 2) {
        const [d, t] = parts;
        const timeTrim = t.substring(0, 5);
        return `${d} ${timeTrim}`;
      }
      return startTime;
    } catch {
      return startTime || `Session #${index + 1}`;
    }
  }

  renderChart() {
    if (this.isDestroyed) return;
    const canvas = this.chartCanvasRef?.nativeElement;
    if (!canvas || !canvas.isConnected) return;
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = undefined;
    }

    const nonDummyRows = this.rowData.filter(r => !r?.['isDummy'] && !r?.['_isTotal']);
    if (nonDummyRows.length === 0) return;
    const recentRows = nonDummyRows.slice(-20);

    const labels = recentRows.map((r, i) => {
      const st = r['start_time'] ?? r['StartTime'] ?? r['start_date'] ?? r['session_id'] ?? '';
      return this.formatSessionLabel(st, i);
    });

    const unitData = recentRows.map(r => {
      const val = parseFloat(r['unit'] ?? r['Unit'] ?? r['units'] ?? r['energy_kwh'] ?? r['consumed_unit'] ?? 0);
      return isNaN(val) ? 0 : val;
    });

    const amountData = recentRows.map(r => {
      const raw = String(r['amount'] ?? r['Amount'] ?? r['cost'] ?? r['session_cost'] ?? 0).replace(/[^0-9.-]/g, '');
      const val = parseFloat(raw);
      return isNaN(val) ? 0 : val;
    });

    const durationMinutesData = recentRows.map(r => {
      const dur = r['run_duration'] ?? r['RunDuration'] ?? r['duration'] ?? '';
      const st = r['start_time'] ?? r['StartTime'] ?? '';
      const sp = r['stop_time'] ?? r['StopTime'] ?? '';
      return this.parseDurationMinutes(dur, st, sp);
    });

    this.ngZone.runOutsideAngular(() => {
      if (this.isDestroyed || !canvas.isConnected) return;
      this.chartInstance = new Chart(canvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              type: 'bar',
              label: 'Energy Consumed (Units / kWh)',
              data: unitData,
              backgroundColor: 'rgba(34, 197, 94, 0.75)',
              borderColor: '#16a34a',
              borderWidth: 1.5,
              borderRadius: 6,
              yAxisID: 'y',
              order: 2,
            },
            {
              type: 'line',
              label: 'Amount Charged (₹)',
              data: amountData,
              borderColor: '#6366f1',
              backgroundColor: 'rgba(99, 102, 241, 0.12)',
              fill: false,
              tension: 0.35,
              pointRadius: 4,
              pointHoverRadius: 7,
              pointBackgroundColor: '#6366f1',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
              borderWidth: 2.5,
              yAxisID: 'y1',
              order: 1,
            },
            {
              type: 'line',
              label: 'Run Duration (Mins)',
              data: durationMinutesData,
              borderColor: '#f59e0b',
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              borderDash: [5, 4],
              fill: false,
              tension: 0.35,
              pointRadius: 3.5,
              pointHoverRadius: 6,
              pointBackgroundColor: '#f59e0b',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
              borderWidth: 2,
              yAxisID: 'y2',
              order: 3,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                boxWidth: 14,
                usePointStyle: true,
                pointStyle: 'circle'
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                title: (items) => {
                  if (!items.length) return '';
                  const idx = items[0].dataIndex;
                  const row = recentRows[idx];
                  const st = row?.['start_time'] ?? row?.['StartTime'] ?? '';
                  const sp = row?.['stop_time'] ?? row?.['StopTime'] ?? '';
                  if (st && sp) {
                    return `Session: ${st} → ${sp}`;
                  }
                  return st ? `Start: ${st}` : (items[0].label || 'Charging Session');
                },
                label: (context) => {
                  const label = context.dataset.label || '';
                  const val = context.parsed.y ?? 0;
                  if (label.includes('Energy')) {
                    return `⚡ ${label}: ${val.toFixed(2)} kWh`;
                  }
                  if (label.includes('Amount')) {
                    return `💰 ${label}: ₹${val.toFixed(2)}`;
                  }
                  if (label.includes('Duration')) {
                    const idx = context.dataIndex;
                    const row = recentRows[idx];
                    const rawDur = row?.['run_duration'] ?? row?.['RunDuration'] ?? '';
                    return `⏱ ${label}: ${rawDur ? rawDur + ' (' + val + ' mins)' : val + ' mins'}`;
                  }
                  return `${label}: ${val}`;
                }
              }
            }
          },
          scales: {
            x: {
              grid: { color: 'rgba(200, 200, 200, 0.15)' },
              ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 12 }
            },
            y: {
              type: 'linear',
              position: 'left',
              beginAtZero: true,
              grid: { color: 'rgba(200, 200, 200, 0.15)' },
              title: { display: true, text: 'Energy (Units / kWh)' }
            },
            y1: {
              type: 'linear',
              position: 'right',
              beginAtZero: true,
              grid: { drawOnChartArea: false },
              title: { display: true, text: 'Amount (₹)' }
            },
            y2: {
              type: 'linear',
              position: 'right',
              beginAtZero: true,
              display: false, // shared right axis scale for duration line without cluttering axis ticks
              grid: { drawOnChartArea: false }
            }
          }
        }
      });
    });
  }
}
